import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { cn } from "@omni/utils/styles";
import { useMemo } from "react";

import { CustomerAddress } from "@/components/Address/Address/address.component";
import { Divider } from "@/components/General/Divider/divider.component";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { OrderDetailsModel } from "@/redux/modules/api/data/orderDetail/@types/orderDetails.model";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { formatISO } from "@/utils/date";

import { PaymentMethods } from "../../shared/Details/PaymentMethod/paymentMethods.container";
import ContactBlock from "./ContactBlock/contactBlock.component";

type Props = {
    orderDetails: OrderDetailsModel;
};

const Details = ({ orderDetails }: Props) => {
    const { t } = useGranite();
    const customer = orderDetails.customer;
    const billingAddress = orderDetails.billingAddress;
    const deliveryAddress = orderDetails.deliveryAddress;
    const collectProducts = useMemo(
        () =>
            orderDetails.items.filter(
                (i) => i.deliveryType === DeliveryType.ClickAndCollect
            ),
        [orderDetails.items]
    );

    const addressBlocks: any[] = [];
    if (billingAddress) {
        addressBlocks.push({
            title: t("orderOverview.label.billingAddress"),
            Component: CustomerAddress,
            props: {
                address: billingAddress,
                addressType: AddressType.Payment
            }
        });
    }
    if (deliveryAddress) {
        addressBlocks.push({
            title: t("orderOverview.title.deliveryAddress"),
            Component: CustomerAddress,
            props: {
                address: deliveryAddress,
                addressType: AddressType.Delivery
            }
        });
    }
    addressBlocks.push({
        title: t("orderOverview.title.contact"),
        Component: ContactBlock,
        props: {
            store: orderDetails.store,
            customer: customer,
            hasCollectProducts: collectProducts && collectProducts.length > 0
        }
    });

    const renderBlock = (index: number) => {
        if (index > addressBlocks.length - 1) {
            return null;
        }

        const { title, Component, props } = addressBlocks[index];

        return (
            <>
                <h5 className="font-weight-bold">{title}</h5>
                <div className="pb-4">
                    <Component {...props} />
                </div>
            </>
        );
    };

    return (
        <>
            <h3 className="mt-4">{t("orderOverview.title.orderDetails")}</h3>
            <Divider />
            <Row>
                <Col
                    xs={12}
                    sm={6}
                    md={4}
                >
                    <h5 className="font-weight-bold">
                        {t("orderOverview.label.orderDate")}
                    </h5>
                    <p>{formatISO(orderDetails.creationDate)}</p>
                </Col>
                <Col
                    xs={12}
                    sm={6}
                    md={4}
                >
                    <h5 className="font-weight-bold">
                        {t("orderOverview.title.paymentMethod")}
                    </h5>
                    <PaymentMethods
                        payments={orderDetails.payments}
                        showAmount={true}
                    />
                </Col>
                {!!orderDetails.invoiceUrl && (
                    <Col
                        xs={12}
                        md={4}
                        className={cn("tw:pb-2 tw:md:py-0 tw:px-0")}
                    >
                        <a href={orderDetails.invoiceUrl || "#"}>
                            <i className="las la-file-download pr-1" />
                            {t("orderOverview.label.download")}
                        </a>
                    </Col>
                )}
            </Row>
            <Row>
                <Col
                    xs={12}
                    md={4}
                    className={cn("tw:text-nowrap")}
                >
                    {renderBlock(0)}
                </Col>
                <Col
                    xs={12}
                    md={4}
                    className={cn("tw:text-nowrap")}
                >
                    {renderBlock(1)}
                </Col>

                <Col
                    xs={12}
                    md={4}
                    className={cn("tw:text-nowrap")}
                >
                    {renderBlock(2)}
                </Col>
            </Row>
        </>
    );
};

export default Details;
